import React from 'react';
import { Link } from 'react-router-dom';
import { useTypingEffect } from '@/hooks/useTypingEffect';

interface MessageDisplayProps {
  content: string;
  isTyping?: boolean;
  messageId?: string;
  onTypingComplete?: (messageId: string) => void;
}

// Matches app routes like /sales-operations/proposals or /brand-products
const ROUTE_REGEX = /(\/(?:[\w-]+\/)*[\w-]+)/g;

function isAppRoute(segment: string): boolean {
  return segment.startsWith('/') && /^\/[\w/-]+$/.test(segment);
}

/** Renders text with path-like segments (e.g. /sales-operations/proposals) as active links */
function textWithRouteLinks(text: string, keyPrefix: string): React.ReactNode[] {
  const parts = text.split(ROUTE_REGEX);
  return parts.map((part, i) => {
    if (isAppRoute(part)) {
      return (
        <Link
          key={`${keyPrefix}-${i}`}
          to={part}
          className="underline text-foreground hover:text-primary font-medium"
        >
          {part}
        </Link>
      );
    }
    return part;
  });
}

export const MessageDisplay: React.FC<MessageDisplayProps> = ({
  content,
  isTyping = false,
  messageId,
  onTypingComplete
}) => {
  const scrollCallback = React.useCallback(() => {
    setTimeout(() => {
      const messagesContainer = document.querySelector('[data-radix-scroll-area-viewport]');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 0);
  }, []);

  const { displayedText, isTyping: isCurrentlyTyping } = useTypingEffect(
    isTyping ? content : '',
    30,
    scrollCallback
  );

  React.useEffect(() => {
    if (isTyping && !isCurrentlyTyping && displayedText === content && messageId && onTypingComplete) {
      onTypingComplete(messageId);
    }
  }, [isTyping, isCurrentlyTyping, displayedText, content, messageId, onTypingComplete]);

  const textToShow = isTyping ? displayedText : content;

  return (
    <div className="text-sm whitespace-pre-wrap">
      {textToShow.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/).map((part, index) => {
        const linkMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
        const boldMatch = part.match(/\*\*([^*]+)\*\*/);

        if (linkMatch) {
          const [, text, route] = linkMatch;
          return (
            <Link
              key={index}
              to={route}
              className="underline text-foreground hover:text-primary font-medium"
            >
              {text}
            </Link>
          );
        }

        if (boldMatch) {
          const [, text] = boldMatch;
          return (
            <strong key={index}>
              {textWithRouteLinks(text, `bold-${index}`)}
            </strong>
          );
        }

        return <React.Fragment key={index}>{textWithRouteLinks(part, `plain-${index}`)}</React.Fragment>;
      })}
      {isTyping && displayedText && <span className="animate-pulse">|</span>}
    </div>
  );
};